<template>
  <div class="face-recognition-root">
    <button
      v-if="!(step === 'result' && result)"
      type="button"
      class="back-btn"
      :disabled="submitting"
      :aria-label="$t('Закрыть')"
      @click="handleBack"
    >
      <svg-icon name="cancel" class="w-5 h-5" />
    </button>

    <div class="scan-area">
      <template v-if="step === 'scan'">
        <AppFaceId
          ref="faceId"
          :external-error="scanError"
          @done="handleDone"
          @error="handleError"
          @close="handleBack"
          @sendFalseResult="handleFalse"
          @timeout="handleTimeout"
        />
        <AppFaceIdProgress
          :step="progressStep"
          :mode="hasError ? 'qr' : 'face'"
        />
      </template>

      <FaceIdResult
        v-else-if="step === 'result'"
        :face-url="faceUrl"
        :result="result"
        :person-name="personName"
        :error-message="lastErrorDetail"
        @retry="onRetry"
        @login="onLogin"
      />
    </div>
  </div>
</template>

<script>
import AppFaceId from "./AppFaceId.vue";
import AppFaceIdProgress from "./AppFaceIdProgress.vue";
import FaceIdResult from "./FaceIdResult.vue";
import { authPort } from "@/shared/auth/authPort";

export default {
  name: "AppFaceRecognition",
  components: { AppFaceId, AppFaceIdProgress, FaceIdResult },

  props: {
    sessionId: { type: String, required: true },
    roleCode: { type: String, required: true },
    personName: { type: String, default: "" },
  },

  emits: ["close", "login", "success"],

  data() {
    return {
      step: "scan",
      progressStep: "right",
      hasError: false,
      submitting: false,
      result: false,
      faceUrl: null,
      scanError: "",
      scanErrorTimer: null,
      attemptCount: 0,
      maxAttempts: 3,
      lastErrorDetail: "",
    };
  },

  beforeUnmount() {
    if (this.scanErrorTimer) clearTimeout(this.scanErrorTimer);
    this.revokeFaceUrl();
  },

  methods: {
    handleBack() {
      if (this.submitting) return;
      this.$emit("close");
    },

    handleError() {
      this.hasError = true;
    },

    handleTimeout() {
      if (this.submitting) return;
      if (this.scanErrorTimer) {
        clearTimeout(this.scanErrorTimer);
        this.scanErrorTimer = null;
      }
      this.scanError = "";
      this.lastErrorDetail = this.$t(
        "Не удалось распознать лицо. Проверьте освещение и положение лица в кадре, затем попробуйте снова."
      );
      this.result = false;
      this.step = "result";
    },

    revokeFaceUrl() {
      if (this.faceUrl) {
        URL.revokeObjectURL(this.faceUrl);
        this.faceUrl = null;
      }
    },

    handleFalse(detections) {
      const blob = detections?.[detections.length - 1];
      this.revokeFaceUrl();
      this.faceUrl = blob ? URL.createObjectURL(blob) : null;
      this.triggerInlineError(this.$t("Не удалось захватить изображение"));
    },

    async handleDone(detections) {
      if (this.submitting) return;
      const blob = detections?.[detections.length - 1];
      if (!blob) {
        this.triggerInlineError(this.$t("Не удалось захватить изображение"));
        return;
      }
      this.revokeFaceUrl();
      this.faceUrl = URL.createObjectURL(blob);
      this.submitting = true;
      this.progressStep = "finally_done";

      try {
        const res = await authPort.nclayer.loginWithFaceId(
          this.sessionId,
          this.roleCode,
          blob
        );
        if (res?._success && res.access_token) {
          this.attemptCount = 0;
          this.lastErrorDetail = "";
          this.result = true;
          this.step = "result";
          this.$emit("success", res);
        } else {
          this.triggerInlineError(
            res?.message || this.$t("Идентификация не прошла")
          );
        }
      } catch (e) {
        const detail = e?.response?.data?.payload?.detail;
        const msg =
          (typeof detail === "string" ? detail : detail?.message) ||
          e?.message ||
          this.$t("Идентификация не прошла");
        this.triggerInlineError(msg);
      } finally {
        this.submitting = false;
      }
    },

    triggerInlineError(message) {
      this.lastErrorDetail = message || "";
      this.attemptCount++;
      if (this.attemptCount >= this.maxAttempts) {
        this.scanError = "";
        if (this.scanErrorTimer) {
          clearTimeout(this.scanErrorTimer);
          this.scanErrorTimer = null;
        }
        this.result = false;
        this.step = "result";
        return;
      }

      this.scanError = message || "";
      this.progressStep = "right";
      if (this.scanErrorTimer) clearTimeout(this.scanErrorTimer);
      this.scanErrorTimer = setTimeout(() => {
        this.scanError = "";
        this.$refs.faceId?.resume();
      }, 3000);
    },

    onRetry() {
      if (this.scanErrorTimer) {
        clearTimeout(this.scanErrorTimer);
        this.scanErrorTimer = null;
      }
      this.revokeFaceUrl();
      this.step = "scan";
      this.progressStep = "right";
      this.result = false;
      this.hasError = false;
      this.scanError = "";
      this.attemptCount = 0;
      this.lastErrorDetail = "";
    },

    onLogin() {
      this.$emit("login");
    },
  },
};
</script>

<style lang="scss" scoped>
.face-recognition-root {
  @apply relative flex flex-col w-full h-full max-w-[600px] mx-auto;
}

.back-btn {
  @apply absolute top-5 left-5 w-10 h-10 rounded-lg flex items-center justify-center z-50;
  @apply cursor-pointer transition-colors bg-transparent border-0;
  color: hsl(var(--foreground));

  &:hover:not(:disabled) {
    background-color: hsl(var(--muted));
  }

  &:disabled {
    @apply opacity-40 cursor-not-allowed;
  }
}

.scan-area {
  @apply flex flex-col flex-1 w-full justify-center;
}
</style>
